import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { NetworkHero } from "@/components/visuals/Network";
import { TrustStrip, ProblemCards, ServiceGroups, IntelligenceCards, HowItWorks, Differentiation, TechLine } from "@/components/blocks1";
import { SolutionsTabs, ArchSection, SecurityPillars, BharatSection, EcosystemPreview, InnovationGrid, MoatSection, ImpactTargets, Validation, VisionMission, Roadmap, FinalCTA } from "@/components/blocks2";
import Founders from "@/components/Founders";
import { Marquee, MaskedLine, Eyebrow, btnPrimary, btnGhost, ArrowIcon, usePageMeta, EASE } from "@/components/primitives";
import { MARQUEE_ITEMS } from "@/lib/data";

function Hero() {
  return (
    <section className="relative flex min-h-screen items-center overflow-hidden bg-grid" data-testid="home-hero">
      <div className="pointer-events-none absolute -top-32 left-1/4 h-[520px] w-[720px] bg-glow-blue" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[420px] w-[520px] bg-glow-teal" aria-hidden="true" />
      <div className="relative mx-auto grid w-full max-w-[1440px] items-center gap-14 px-6 pb-24 pt-36 lg:grid-cols-12 lg:px-8 lg:pt-40">
        <div className="lg:col-span-7">
          <motion.div
            className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <img src="/assets/timepe-logo.png" alt="TimePe" className="h-16 w-auto object-contain" />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-mute">
              Flagship platform of Avyagrah Tech Innovations Pvt. Ltd.
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mt-8"
          >
            <Eyebrow>Avyagrah Tech Innovations — Engineering Tomorrow with Intelligence</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight text-mist">
            <MaskedLine delay={0.25} mount>Secure for</MaskedLine>
            <MaskedLine delay={0.37} mount><span className="text-teal">Bharat.</span></MaskedLine>
            <MaskedLine delay={0.49} mount className="text-mute">Built in Bharat,</MaskedLine>
            <MaskedLine delay={0.61} mount className="text-mute">for Bharat.</MaskedLine>
          </h1>

          <motion.p
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-mute"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          >
            Building sovereign, AI-powered financial infrastructure for a safer, smarter and more
            trusted digital financial ecosystem.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          >
            <Link to="/solutions" data-testid="hero-cta-explore" className={btnGhost}>
              Explore TimePe <ArrowIcon />
            </Link>
            <Link to="/partners" data-testid="hero-cta-partner" className={btnPrimary}>
              Partner with Us <ArrowIcon />
            </Link>
          </motion.div>

          <motion.div
            className="mt-12 max-w-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
          >
            <TechLine />
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
        >
          <NetworkHero />
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        aria-hidden="true"
      >
        <ChevronDown className="h-5 w-5 animate-bounce text-mute" />
      </motion.div>
    </section>
  );
}

export default function Home() {
  usePageMeta(
    "TimePe | AI-Powered Financial Infrastructure for Bharat",
    "TimePe by Avyagrah Tech Innovations is building secure, intelligent and inclusive financial infrastructure powered by AI, DeepTech, fraud intelligence, RegTech and privacy-by-design."
  );
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <TrustStrip />
      <ProblemCards />
      <ServiceGroups />
      <IntelligenceCards />
      <HowItWorks />
      <Differentiation />
      <SolutionsTabs />
      <ArchSection />
      <SecurityPillars />
      <BharatSection />
      <EcosystemPreview />
      <InnovationGrid />
      <MoatSection />
      <ImpactTargets />
      <Validation />
      <Founders />
      <VisionMission />
      <Roadmap />
      <FinalCTA />
    </>
  );
}
